import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { Tooltip } from "radix-ui";
import type { ReactElement } from "react";
import type { CountStyle, NavItem } from "@/shared/components/layout/sidebar-nav.config";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/shared/lib/cn";

/// One sidebar nav row, a Link (or disabled/locked span/button), with active
/// indicator, count/new-dot/"Soon"/lock trailing affordances, and a
/// collapsed-rail tooltip. Shared by the flat sections and the collapsible
/// Pulse group.
///
/// A plan-disabled feature renders as a locked row (greyed text and icon,
/// Lock glyph, collapsed-rail lock badge) instead of hiding (W4):
/// `resolveNavItemGate` (sidebar-nav.gate.ts) marks the item `locked` and
/// `sidebar-nav.tsx` wires `onLockedClick` to open the shared
/// `<UpgradeDialog>`.
export function Item({
  item,
  resolvedHref,
  isActive,
  collapsed,
  onLockedClick,
}: {
  item: NavItem;
  resolvedHref: string;
  isActive: boolean;
  collapsed: boolean;
  /** Only meaningful when `item.locked` is set (W4): opens the upgrade
   *  prompt for the item's gated feature. Bound per-item by
   *  `lockedClickHandler` (sidebar-nav.gate.ts) at the `sidebar-nav.tsx`
   *  call site. */
  onLockedClick?: () => void;
}): ReactElement {
  const Icon = item.icon;

  const sharedClasses = cn(
    "relative items-center rounded-md transition-colors duration-150 ease-out",
    "text-sm font-medium outline-none focus-visible:shadow-ring-focus",
    collapsed
      ? "grid size-9 place-items-center"
      : "grid grid-cols-[18px_1fr_auto] gap-2.5 px-2.5 py-1.5",
  );

  const content = (
    <>
      {isActive ? (
        <span
          aria-hidden
          className={cn(
            "bg-pulse absolute w-0.5 rounded-full",
            collapsed ? "left-0 top-1.5 bottom-1.5" : "-left-3 top-2 bottom-2",
          )}
        />
      ) : null}
      <Icon
        className={cn(
          "size-4 transition-colors",
          item.locked
            ? "text-text-quaternary"
            : isActive
              ? "text-text-brand"
              : "text-text-tertiary",
        )}
      />
      {/* Icon-rail mode drops the label (and with it the inline Lock glyph
         below), so a locked item would otherwise read as "just a dimmer
         icon", a color-only cue. This badge keeps the lock shape visible
         without relying on the shade difference. */}
      {collapsed && item.locked ? (
        <span
          aria-hidden
          data-slot="locked-badge"
          className="border-surface-subtle bg-surface-subtle absolute bottom-1 right-1 grid size-3.5 place-items-center rounded-full border"
        >
          <Lock className="size-2 text-text-quaternary" />
        </span>
      ) : null}
      {collapsed ? null : (
        <>
          <span className={cn("truncate tracking-tight", item.locked && "text-text-quaternary")}>
            {item.label}
          </span>
          {/* No trailing glyph on a locked row. The dimmed label and icon
              already read as unavailable, and the lock sat in the `auto`
              grid column, which visually pulled the row apart from its
              neighbours for the sake of a cue the colour already carries.
              Locked rows also skip counts and activity dots below: a badge
              on something you cannot open is noise. */}
          {item.locked ? null : item.count ? (
            <Count value={item.count.value} style={item.count.style ?? "neutral"} />
          ) : item.newActivity ? (
            <NewDot />
          ) : item.disabled ? (
            <span className="font-mono text-4xs uppercase tracking-wider text-text-quaternary">
              Soon
            </span>
          ) : item.beta ? (
            <Badge className="border-0 bg-violet-100 px-1.5 text-3xs font-semibold uppercase tracking-wider text-violet-700 dark:bg-violet-600/20 dark:text-violet-300">
              Beta
            </Badge>
          ) : null}
        </>
      )}
    </>
  );

  // `aria-disabled` (not the native `disabled` attribute) is deliberate: the
  // row must stay focusable and clickable so it can open the upgrade prompt,
  // native `disabled` would block both. Support for announcing
  // `aria-disabled` varies by assistive technology, so it can't carry the
  // "this is locked, and clicking it opens an upgrade dialog instead of
  // navigating" meaning on its own. Collapsed mode gets that meaning from
  // the tooltip's "(locked)" description (`withCollapsedTooltip`,
  // `aria-describedby`); expanded mode has no tooltip, so it needs its own
  // `aria-label` naming both facts, the visible label stays unchanged.
  if (item.locked) {
    const el = (
      <button
        type="button"
        aria-disabled="true"
        aria-label={collapsed ? undefined : lockedAriaLabel(item.label)}
        onClick={onLockedClick}
        className={cn(
          sharedClasses,
          // text-left because a <button> centres its content by default,
          // unlike the <a> and <span> the other two branches render. Without
          // it a locked row's label sat centred while every row above it was
          // left-aligned.
          "cursor-pointer text-left text-text-quaternary hover:bg-muted/60",
        )}
      >
        {content}
      </button>
    );
    return withCollapsedTooltip(collapsed, item.label, el, true);
  }

  if (item.disabled) {
    const el = (
      <span aria-disabled className={cn(sharedClasses, "cursor-not-allowed text-text-quaternary")}>
        {content}
      </span>
    );
    return withCollapsedTooltip(collapsed, item.label, el);
  }

  const link = (
    <Link
      // reason: nav items mix static paths and workspace-templated paths
      // (resolved to a concrete URL by the caller). TanStack Router's Link
      // accepts a plain string href as long as it matches a registered route
      // at runtime; we pass the resolved string here rather than splitting
      // into `to` + `params` per item.
      to={resolvedHref as never}
      data-sidebar-module={item.moduleId}
      className={cn(
        sharedClasses,
        "text-text-secondary hover:bg-muted hover:text-foreground",
        isActive && "bg-card text-foreground shadow-xs",
      )}
    >
      {content}
    </Link>
  );

  return withCollapsedTooltip(collapsed, item.beta ? `${item.label} (Beta)` : item.label, link);
}

/** Wraps `el` in a right-anchored Tooltip when the sidebar is collapsed.
 *  `locked` appends a "(locked)" suffix so the tooltip carries the gated
 *  state too, the collapsed row itself only has icon shape + color to work
 *  with (no label, see the `locked-badge` above). */
function withCollapsedTooltip(
  collapsed: boolean,
  label: string,
  el: ReactElement,
  locked = false,
): ReactElement {
  if (!collapsed) return el;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{el}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={10}
          className="z-50 rounded-md bg-zinc-900 px-2 py-1 text-2xs font-medium text-white shadow-md dark:bg-zinc-100 dark:text-zinc-900"
        >
          {collapsedTooltipLabel(label, locked)}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/** Pure so the "(locked)" suffix decision is unit-testable without
 *  rendering the Tooltip/Portal tree. */
export function collapsedTooltipLabel(label: string, locked: boolean): string {
  return locked ? `${label} (locked)` : label;
}

/** Accessible name for an expanded locked row. The visible label alone says
 *  nothing about why activating the row does not navigate, so this names
 *  both facts for assistive technology: the item is locked, and activating
 *  it opens upgrade options rather than the feature. */
export function lockedAriaLabel(label: string): string {
  return `${label} (locked, opens upgrade options)`;
}

function Count({ value, style }: { value: number; style: CountStyle }): ReactElement {
  return (
    <span
      className={cn(
        "min-w-[18px] rounded-full px-1.5 text-center font-mono text-3xs font-semibold",
        style === "brand" &&
          "bg-violet-100 text-violet-700 dark:bg-violet-600/20 dark:text-violet-300",
        style === "alert" && "bg-danger-bg text-danger-fg",
        style === "neutral" && "bg-muted text-text-tertiary",
      )}
    >
      {value}
    </span>
  );
}

function NewDot(): ReactElement {
  return (
    <span
      role="img"
      aria-label="New activity"
      className="size-1.5 rounded-full bg-pink-500 [box-shadow:0_0_0_3px_rgba(236,72,153,0.18)]"
    />
  );
}
